/**
 * Two-finger gestures: `MagnifyGesture` and `RotateGesture` (and their iOS 13
 * forms). A `gesture` element whose props set `magnify` or `rotate` gets
 * `data-sb-pinch` (renderer.ts) and these pinch sources:
 *
 * - two touch (or pen) pointers on it: the scale is the distance between them
 *   over the distance when the second came down, the rotation the turn of the
 *   line between them, the location their midpoint;
 * - a trackpad pinch, which Chrome, Edge and Firefox report as `wheel` events
 *   with `ctrlKey` (a scale only);
 * - Safari's `gesturestart` / `gesturechange` / `gestureend` on a computer
 *   (scale and rotation). On a touch screen Safari sends them alongside the
 *   pointers, so there they only stop the page zooming.
 *
 * The gesture begins once the scale has moved `minimumScaleDelta` (with
 * `magnify`) or the angle `minimumAngleDelta` degrees (with `rotate`), then
 * sends `pinch` events: `began`, `changed` (one per animation frame) and
 * `ended`, or `cancelled` when the browser takes a pointer away
 * (docs/ops-protocol.md, "Pinch"). The single-pointer gestures of the same
 * element (a drag) carry on beside it, as SwiftUI's simultaneous gestures do.
 */

import type { DragPhase, ElementId, PinchEvent } from './protocol';

export interface PinchHost {
  readonly root: HTMLElement;
  send(event: PinchEvent): void;
  nodeOf(id: ElementId): { props: Record<string, unknown> } | undefined;
}

/** The element a pinch is on and its thresholds. */
interface Target {
  el: HTMLElement;
  id: ElementId;
  magnify: boolean;
  rotate: boolean;
  minScale: number;
  minAngle: number;
}

/** A pinch in progress, from whichever source. */
interface Pinch {
  target: Target;
  began: boolean;
  scale: number;
  /** Degrees, unwrapped (a full turn and more keeps counting). */
  rotation: number;
  startLocation: { x: number; y: number };
  startAnchor: { x: number; y: number };
  /** Where the fingers' midpoint is now, in the element's coordinates. */
  location: { x: number; y: number };
  /** The element's top leading corner in the viewport when the pinch began. */
  origin: { x: number; y: number };
  samples: { t: number; scale: number; rotation: number }[];
  raf: number | null;
}

/** Two pointers on one element, and the pinch they drive once both are down. */
interface PointerPinch {
  target: Target;
  points: Map<number, { x: number; y: number }>;
  /** The two pointers' ids, distance and angle when the second came down. */
  base: { a: number; b: number; distance: number; angle: number } | null;
  /** The last raw angle (degrees), for unwrapping. */
  lastAngle: number;
  pinch: Pinch | null;
}

const WHEEL_END_MS = 150;
const VELOCITY_WINDOW_MS = 100;
/** How much one pixel of a ctrl+wheel pinch scales (Chrome sends small deltas per frame). */
const WHEEL_SCALE_PER_PIXEL = 0.01;

const round = (n: number, places = 4): number => {
  const f = 10 ** places;
  return Math.round(n * f) / f;
};

interface SafariGestureEvent extends UIEvent {
  scale: number;
  rotation: number;
  clientX: number;
  clientY: number;
}

export class PinchController {
  private pointers: PointerPinch | null = null;
  private wheel: { pinch: Pinch; timer: number } | null = null;
  private safari: Pinch | null = null;
  /** Safari on a computer sends gesture events for the trackpad; a touch screen sends them beside the pointers. */
  private readonly safariGestures = typeof navigator !== 'undefined' && navigator.maxTouchPoints === 0;

  constructor(private readonly host: PinchHost) {
    const root = host.root;
    root.addEventListener('pointerdown', this.onPointerDown, true);
    root.addEventListener('wheel', this.onWheel, { capture: true, passive: false });
    root.addEventListener('gesturestart', this.onGestureStart, true);
    root.addEventListener('gesturechange', this.onGestureChange, true);
    root.addEventListener('gestureend', this.onGestureEnd, true);
  }

  dispose(): void {
    const root = this.host.root;
    root.removeEventListener('pointerdown', this.onPointerDown, true);
    root.removeEventListener('wheel', this.onWheel, true);
    root.removeEventListener('gesturestart', this.onGestureStart, true);
    root.removeEventListener('gesturechange', this.onGestureChange, true);
    root.removeEventListener('gestureend', this.onGestureEnd, true);
    this.stopPointers();
    if (this.wheel) window.clearTimeout(this.wheel.timer);
    this.wheel = null;
    this.safari = null;
  }

  /** The innermost pinch gesture element at or above `node`, with its props. */
  private targetAt(node: EventTarget | null): Target | null {
    const start = node instanceof Element ? node : null;
    const el = start?.closest<HTMLElement>('.sb-gesture[data-sb-pinch]') ?? null;
    if (!el || !this.host.root.contains(el)) return null;
    const id = Number(el.dataset['sbId']);
    if (!Number.isInteger(id)) return null;
    const props = this.host.nodeOf(id)?.props ?? {};
    const magnify = props['magnify'] === true;
    const rotate = props['rotate'] === true;
    if (!magnify && !rotate) return null;
    const minScale = typeof props['minimumScaleDelta'] === 'number' ? Math.max(0, props['minimumScaleDelta']) : 0.01;
    const minAngle = typeof props['minimumAngleDelta'] === 'number' ? Math.max(0, props['minimumAngleDelta']) : 1;
    return { el, id, magnify, rotate, minScale, minAngle };
  }

  private newPinch(target: Target, clientX: number, clientY: number): Pinch {
    const rect = target.el.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    return {
      target,
      began: false,
      scale: 1,
      rotation: 0,
      startLocation: { x: round(x, 2), y: round(y, 2) },
      startAnchor: { x: round(rect.width > 0 ? x / rect.width : 0.5), y: round(rect.height > 0 ? y / rect.height : 0.5) },
      location: { x: round(x, 2), y: round(y, 2) },
      origin: { x: rect.left, y: rect.top },
      samples: [],
      raf: null,
    };
  }

  /** Records a new scale and rotation; begins the gesture past its thresholds, else queues a `changed`. */
  private update(pinch: Pinch, scale: number, rotation: number, time: number): void {
    pinch.scale = scale;
    pinch.rotation = rotation;
    pinch.samples.push({ t: time, scale, rotation });
    while (pinch.samples.length > 2 && time - pinch.samples[0]!.t > VELOCITY_WINDOW_MS) pinch.samples.shift();
    const { target } = pinch;
    if (!pinch.began) {
      const scaled = target.magnify && Math.abs(scale - 1) >= target.minScale;
      const turned = target.rotate && Math.abs(rotation) >= target.minAngle;
      if (!scaled && !turned) return;
      pinch.began = true;
      this.send(pinch, 'began');
      return;
    }
    if (pinch.raf !== null) return;
    pinch.raf = requestAnimationFrame(() => {
      pinch.raf = null;
      this.send(pinch, 'changed');
    });
  }

  /** Ends a pinch that began (a pinch below its thresholds sends nothing). */
  private finish(pinch: Pinch, phase: 'ended' | 'cancelled'): void {
    if (pinch.raf !== null) cancelAnimationFrame(pinch.raf);
    pinch.raf = null;
    if (pinch.began) this.send(pinch, phase);
  }

  private send(pinch: Pinch, phase: DragPhase): void {
    const first = pinch.samples[0];
    const last = pinch.samples[pinch.samples.length - 1];
    const dt = first && last ? (last.t - first.t) / 1000 : 0;
    const scaleVelocity = first && last && dt > 0 ? (last.scale - first.scale) / dt : 0;
    const rotationVelocity = first && last && dt > 0 ? (last.rotation - first.rotation) / dt : 0;
    this.host.send({
      type: 'pinch',
      id: pinch.target.id,
      phase,
      scale: round(pinch.scale),
      rotation: round(pinch.rotation, 2),
      scaleVelocity: round(scaleVelocity),
      rotationVelocity: round(rotationVelocity, 2),
      startLocation: pinch.startLocation,
      startAnchor: pinch.startAnchor,
      location: pinch.location,
    });
  }

  // ---------------------------------------------------------------------------
  // Two pointers
  // ---------------------------------------------------------------------------

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (event.pointerType === 'mouse') return;
    const target = this.targetAt(event.target);
    if (!target) return;
    let state = this.pointers;
    if (state && state.target.el !== target.el) {
      // A finger on another pinch element: only one pinch at a time.
      if (state.points.size > 0) return;
      this.stopPointers();
      state = null;
    }
    if (!state) {
      state = { target, points: new Map(), base: null, lastAngle: 0, pinch: null };
      this.pointers = state;
      window.addEventListener('pointermove', this.onPointerMove, true);
      window.addEventListener('pointerup', this.onPointerUp, true);
      window.addEventListener('pointercancel', this.onPointerCancel, true);
    }
    if (state.points.size >= 2) return;
    state.points.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (state.points.size === 2) {
      const [[a, pa], [b, pb]] = [...state.points.entries()] as [[number, { x: number; y: number }], [number, { x: number; y: number }]];
      const distance = Math.hypot(pb.x - pa.x, pb.y - pa.y);
      const angle = (Math.atan2(pb.y - pa.y, pb.x - pa.x) * 180) / Math.PI;
      state.base = { a, b, distance: Math.max(distance, 1), angle };
      state.lastAngle = angle;
      state.pinch = this.newPinch(target, (pa.x + pb.x) / 2, (pa.y + pb.y) / 2);
    }
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    const state = this.pointers;
    if (!state || !state.points.has(event.pointerId)) return;
    state.points.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const { base, pinch } = state;
    if (!base || !pinch) return;
    const pa = state.points.get(base.a);
    const pb = state.points.get(base.b);
    if (!pa || !pb) return;
    const angle = (Math.atan2(pb.y - pa.y, pb.x - pa.x) * 180) / Math.PI;
    // Unwrap across ±180° so a turn keeps counting.
    let step = angle - state.lastAngle;
    if (step > 180) step -= 360;
    else if (step < -180) step += 360;
    state.lastAngle = angle;
    const rotation = pinch.rotation + step;
    const scale = Math.hypot(pb.x - pa.x, pb.y - pa.y) / base.distance;
    pinch.location = { x: round((pa.x + pb.x) / 2 - pinch.origin.x, 2), y: round((pa.y + pb.y) / 2 - pinch.origin.y, 2) };
    this.update(pinch, scale, rotation, event.timeStamp);
  };

  private readonly onPointerUp = (event: PointerEvent): void => this.liftPointer(event, 'ended');
  private readonly onPointerCancel = (event: PointerEvent): void => this.liftPointer(event, 'cancelled');

  /** One of the fingers lifted: the pinch ends; a new second finger starts another. */
  private liftPointer(event: PointerEvent, phase: 'ended' | 'cancelled'): void {
    const state = this.pointers;
    if (!state || !state.points.has(event.pointerId)) return;
    state.points.delete(event.pointerId);
    if (state.pinch) this.finish(state.pinch, phase);
    state.pinch = null;
    state.base = null;
    if (state.points.size === 0) this.stopPointers();
  }

  private stopPointers(): void {
    if (this.pointers?.pinch) this.finish(this.pointers.pinch, 'cancelled');
    this.pointers = null;
    window.removeEventListener('pointermove', this.onPointerMove, true);
    window.removeEventListener('pointerup', this.onPointerUp, true);
    window.removeEventListener('pointercancel', this.onPointerCancel, true);
  }

  // ---------------------------------------------------------------------------
  // Trackpads
  // ---------------------------------------------------------------------------

  /** A trackpad pinch in Chrome, Edge and Firefox: a wheel with ctrl. */
  private readonly onWheel = (event: WheelEvent): void => {
    if (!event.ctrlKey || this.safari) return;
    const current = this.wheel;
    const target = current?.pinch.target ?? this.targetAt(event.target);
    if (!target || !target.magnify) return;
    // The page would zoom.
    event.preventDefault();
    let state = current;
    if (!state) {
      state = { pinch: this.newPinch(target, event.clientX, event.clientY), timer: 0 };
      this.wheel = state;
    } else {
      window.clearTimeout(state.timer);
    }
    const pixels = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
    const scale = state.pinch.scale * Math.exp(-pixels * WHEEL_SCALE_PER_PIXEL);
    this.update(state.pinch, scale, 0, event.timeStamp);
    const ending = state;
    state.timer = window.setTimeout(() => {
      if (this.wheel !== ending) return;
      this.wheel = null;
      this.finish(ending.pinch, 'ended');
    }, WHEEL_END_MS);
  };

  /** Safari's own pinch and rotation events. */
  private readonly onGestureStart = (event: Event): void => {
    const target = this.targetAt(event.target);
    if (!target) return;
    // The page would zoom (on a touch screen too, where the pointers drive the pinch).
    event.preventDefault();
    if (!this.safariGestures) return;
    const gesture = event as SafariGestureEvent;
    this.safari = this.newPinch(target, gesture.clientX, gesture.clientY);
  };

  private readonly onGestureChange = (event: Event): void => {
    if (this.targetAt(event.target)) event.preventDefault();
    const pinch = this.safari;
    if (!pinch) return;
    event.preventDefault();
    const gesture = event as SafariGestureEvent;
    this.update(pinch, gesture.scale, gesture.rotation, event.timeStamp);
  };

  private readonly onGestureEnd = (event: Event): void => {
    if (this.targetAt(event.target)) event.preventDefault();
    const pinch = this.safari;
    if (!pinch) return;
    this.safari = null;
    this.finish(pinch, 'ended');
  };
}
